import styled from "@emotion/styled";

/**
 * Floating dismiss button pinned to the top-right of the sheet panel.
 *
 * Sits outside `SheetContent`'s scroll flow so it stays visible while
 * the user scrolls the sheet body — `StyledSheetModal` (from the shared
 * Modal) has `position: relative`, so this button anchors to the panel
 * corner rather than the scrolling content.
 */
export const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.12);
  color: var(--color-charcoal, #212121);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background: rgba(33, 33, 33, 0.18);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }
`;

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const AlwaysOnPill = styled.span`
  display: inline-flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 120px;
  background: var(--Secondary-MidPurpleHaze, #e4d1f3);
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.005em;
  white-space: nowrap;
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

/**
 * Full-sheet-width hairline that visually separates the header block
 * (avatar + title + subtitle) from the section content below.
 *
 * Uses negative horizontal margins that match `BottomSheet`'s
 * `SheetContent` padding (`1.6rem`) so the rule bleeds edge-to-edge on
 * both mobile and the desktop-fullscreen variant.
 */
export const Divider = styled.hr`
  border: 0;
  border-top: 1px solid rgba(33, 33, 33, 0.08);
  height: 0;
  margin: 1.25rem -1.6rem 0;
`;

export const Title = styled.h4`
  margin: 0;
`;

export const Subtitle = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
`;

export const Section = styled.section`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Eyebrow = styled.p`
  margin: 0;
  color: var(--color-charcoal-60);
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const Body = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
`;

export const PillList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/**
 * Pill chip in the "Where they help" list.
 *
 * Explicitly cancels the global \`ul li\` bullet styling shipped by
 * \`globals-user-app.css\` (line 966) — that rule adds an orange
 * ::before dot and 1.3em left padding to every list item in the app, so
 * without these overrides pills would render with a stray dot and extra
 * indent.
 */
export const Pill = styled.li`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--color-cream-darker);
  color: var(--color-charcoal, #212121);
  font-family: var(--font-family);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.01em;
  list-style: none;
  margin: 0;

  &::before {
    content: none;
  }
`;

export const Note = styled.aside`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-top: 1.5rem;
  font-weight: 500;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--Primary-PurpleHaze, #c294ff);
  background:
    linear-gradient(
      0deg,
      rgba(95, 31, 143, 0.12) 0%,
      rgba(95, 31, 143, 0.12) 100%
    ),
    #fff;
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  box-sizing: border-box;

  & > svg {
    flex-shrink: 0;
  }
`;

export const NoteText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 1.75rem;
`;

export const Disclaimer = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
  text-align: center;
`;
